// Runs — every supervisor run, newest first (SM-538 / SM-546).
//
// Preset NAMES lead. The list used to fetch 200 presets purely to map
// ids to names client-side, which meant a run whose preset had been
// renamed showed the NEW name (rewriting history) and a run whose
// preset had been deleted showed a truncated id. The server now sends
// a resolved label with every run, so this page renders what it is
// given and cannot disagree with the dashboard or the account rows.
//
// Filters are URL-persisted, so a refresh or a shared link lands on
// the same view.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ROUTE_CRITICAL_KEYS, refreshOnNavigate } from "@/lib/routeRefresh";
import { DataFreshness } from "@/components/ui/DataFreshness";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { ModeBadge } from "@/components/runs/RunStateTabs";
import { NameSourceBadge } from "@/components/runs/PresetIdentity";
import { RunHistoryFilters, filterSummary } from "@/components/runs/RunHistoryFilters";
import {
  isModeFilter,
  isStatusGroup,
  statusesParam,
  type ModeFilter,
  type RunHistoryFilterState,
  type StatusGroup,
} from "@/lib/runHistory";
import { fetchRunHistory, keys } from "@/lib/queries";
import { useI18n } from "@/i18n/I18nProvider";
import { runReasonLabel } from "@/i18n/runtimeCopy";
import { strategyNameLabel } from "@/i18n/strategyCopy";

interface RunsSearch {
  status?: StatusGroup;
  mode?: ModeFilter;
  runId?: string;
  from?: string;
  to?: string;
  page?: number;
}

/**
 * Unknown values fall back to the unfiltered view rather than throwing.
 * A hand-edited or stale link should show the operator everything and
 * let them re-filter, not a router error page — and silently keeping an
 * unrecognised value would send it to the server, which answers 400.
 */
export const Route = createFileRoute("/_app/runs/")({
  // SM-563 — re-read this page's data on arrival.
  loader: refreshOnNavigate(ROUTE_CRITICAL_KEYS.runs),
  validateSearch: (search: Record<string, unknown>): RunsSearch => ({
    status: isStatusGroup(search.status) ? search.status : "all",
    mode: isModeFilter(search.mode) ? search.mode : "all",
    runId: typeof search.runId === "string" && search.runId ? search.runId : undefined,
    from: typeof search.from === "string" && search.from ? search.from : undefined,
    to: typeof search.to === "string" && search.to ? search.to : undefined,
    page: typeof search.page === "number" && search.page > 0 ? Math.floor(search.page) : 0,
  }),
  component: RunsPage,
});

const PAGE_SIZE = 50;

function RunsPage() {
  const { t, formatDateTime } = useI18n();
  const raw = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const filters: RunHistoryFilterState = {
    status: raw.status ?? "all",
    mode: raw.mode ?? "all",
    runID: raw.runId ?? "",
    from: raw.from ?? "",
    to: raw.to ?? "",
  };
  const page = raw.page ?? 0;

  const query = {
    statuses: statusesParam(filters.status),
    trading_mode: filters.mode === "all" ? undefined : filters.mode,
    run_id: filters.runID || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const q = useQuery({
    queryKey: keys.presetRuns(query),
    queryFn: () => fetchRunHistory(query),
    placeholderData: (prev) => prev,
    refetchInterval: 5_000,
  });

  function setSearch(patch: Partial<RunsSearch>) {
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  }

  const rows = q.data?.runs ?? [];

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="page-title">{t("runs.title")}</h1>
          <p className="page-subtitle">{t("runs.subtitle")}</p>
        </div>
        <Link to="/presets">
          <Button>{t("runs.startFromPreset")}</Button>
        </Link>
      </header>
      <DataFreshness query={q} />

      <Card>
        <RunHistoryFilters
          value={filters}
          onChange={(next) =>
            setSearch({
              status: next.status,
              mode: next.mode,
              runId: next.runID || undefined,
              from: next.from || undefined,
              to: next.to || undefined,
              page: 0,
            })
          }
        />
        {/* Two rows for one account is expected, not a duplicate. */}
        <p className="mt-2 text-xs t-mute">{t("runs.concurrentNote")}</p>
      </Card>

      <ErrorBanner error={q.error} fallbackTitle={t("presetDetail.error.history")} />

      {q.isLoading && !q.data && (
        <Card>
          <Skeleton lines={6} />
        </Card>
      )}

      {q.data && rows.length === 0 && (
        <Card>
          <div className="empty">
            {filters.status !== "all" ||
            filters.mode !== "all" ||
            filters.runID ||
            filters.from ||
            filters.to ? (
              <span>{t("history.emptyFiltered", { filters: filterSummary(filters, t) })}</span>
            ) : (
              <span>{t("runs.emptyStart")}</span>
            )}
          </div>
        </Card>
      )}

      {q.data && rows.length > 0 && (
        <Card className="section-card">
          <div className="p-0">
            <table className="tbl rounded-none border-0">
              <thead>
                <tr>
                  <th>{t("runs.run")}</th>
                  <th>{t("common.status")}</th>
                  <th>{t("runState.col.mode")}</th>
                  <th>{t("presets.strategy")}</th>
                  <th>{t("overview.started")}</th>
                  <th>{t("overview.stopped")}</th>
                  <th>{t("runs.reason")}</th>
                  <th>{t("history.col.snapshot")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.run_id}>
                    <td>
                      {/* The NAME is the link text. The id below it is
                          for copying into a log search, not for
                          identifying the bot. */}
                      <div className="flex items-center gap-1.5">
                        <Link
                          to="/runs/$id"
                          params={{ id: r.run_id }}
                          className="block max-w-72 truncate text-t-1 hover:t-emph"
                        >
                          {r.preset_name || r.run_id}
                        </Link>
                        <NameSourceBadge source={r.preset_name_source} />
                        {(r.recovery?.attempt ?? 1) > 1 && (
                          <Badge tone="warn" title={r.recovery?.resumed_from_run_id}>
                            {t("history.attemptOf", {
                              attempt: r.recovery.attempt,
                              source: r.recovery.resumed_from_run_id?.slice(0, 8) ?? "—",
                            })}
                          </Badge>
                        )}
                      </div>
                      <Link
                        to="/runs/$id"
                        params={{ id: r.run_id }}
                        className="mono mt-0.5 block max-w-72 truncate text-[11px] text-t-4 hover:t-emph"
                        title={r.run_id}
                      >
                        {r.run_id}
                      </Link>
                    </td>
                    <td>
                      <StatusBadge status={r.status} />
                    </td>
                    <td>
                      <ModeBadge mode={r.trading_mode} />
                    </td>
                    <td className="text-xs">{strategyNameLabel(r.config.strategy.name, t)}</td>
                    <td className="text-xs t-mute">{r.start_at ? formatDateTime(r.start_at) : "—"}</td>
                    <td className="text-xs t-mute">{r.stop_at ? formatDateTime(r.stop_at) : "—"}</td>
                    <td className="text-xs">{runReasonLabel(r.stop_reason, t)}</td>
                    <td className="text-xs">
                      {r.snapshot ? (
                        r.snapshot.complete ? (
                          <Badge tone="yes">{t("history.snapshotCaptured")}</Badge>
                        ) : (
                          <Badge tone="warn">{t("runState.completeness.partial")}</Badge>
                        )
                      ) : (
                        <Badge tone="neutral">{t("runState.completeness.notCaptured")}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {q.data && (q.data.page_info.has_more || page > 0) && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="ghost" disabled={page === 0} onClick={() => setSearch({ page: page - 1 })}>
            {t("common.prev")}
          </Button>
          <span className="mono text-2xs t-mute">
            {t("runState.pageRange", {
              start: q.data.page_info.total === 0 ? 0 : page * PAGE_SIZE + 1,
              end: Math.min(q.data.page_info.total, (page + 1) * PAGE_SIZE),
              total: q.data.page_info.total,
            })}
          </span>
          <Button
            variant="ghost"
            disabled={!q.data.page_info.has_more}
            onClick={() => setSearch({ page: page + 1 })}
          >
            {t("common.next")}
          </Button>
        </div>
      )}
    </div>
  );
}
